import { useEffect, useState } from "react";
import { Button, Form, InputGroup, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";

import { CustomModal } from "../../../../components/CustomModal";
import { WarningIcon } from "../../../../components/icons";
import type { IBillingSettingsWithValues } from "../../../../hooks/reactQueryHooks/useBilling";
import { usePatchBillingSettings } from "../../../../hooks/reactQueryHooks/useBilling";

interface AutoRechargeModalProps {
  organization: string;
  settings: IBillingSettingsWithValues;
  accBallance: number;
  hideModal: () => void;
  refetch: () => void;
}

const AutoRechargeModal: React.FC<AutoRechargeModalProps> = ({
  organization,
  settings,
  accBallance,
  hideModal,
  refetch,
}) => {
  const {
    chargeWhen = 0,
    isAutoChargeEnabled = false,
    expectedAmountOnTheAccount = 0,
    maxChargeWhenValue = 0,
    minChargeWhenValue = 0,
    maxExpectedAmountValue = 0,
    minimalExpectedTransaction = 0,
  } = settings || {};

  const [chargeWhenAmount, setChargeWhenAmount] = useState(chargeWhen);
  const [expectedAmount, setExpectedAmount] = useState(
    expectedAmountOnTheAccount,
  );
  const [isEnabled, setIsEnabled] = useState(isAutoChargeEnabled);
  const [isValid, setIsValid] = useState({
    expectedAmount: true,
    chargeWhenAmount: true,
  });

  const { mutate, isError, isLoading, isSuccess } = usePatchBillingSettings();

  const handleChangeAutocharge = () => {
    setIsEnabled(!isEnabled);
  };

  const handleChangeChargeWhenAmount = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = Number(e.target.value);
    setChargeWhenAmount(value >= 0 ? value : 0);
    setIsValid({
      chargeWhenAmount:
        value >= minChargeWhenValue && value <= maxChargeWhenValue,
      expectedAmount: value + minimalExpectedTransaction <= expectedAmount,
    });
  };

  const handleChangeExpectedAmount = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = Number(e.target.value);
    setExpectedAmount(value >= 0 ? value : 0);
    setIsValid({
      ...isValid,
      expectedAmount:
        value <= maxExpectedAmountValue &&
        value - minimalExpectedTransaction >= chargeWhenAmount,
    });
  };

  const updateBillingSettings = async () => {
    await mutate({
      organizationId: organization,
      settings: {
        isAutoChargeEnabled: isEnabled,
        expectedAmountOnTheAccount: expectedAmount,
        chargeWhen: chargeWhenAmount,
      },
    });
  };

  // disable mouse scroll on number input
  const onScroll = (e: React.WheelEvent<HTMLInputElement>) => {
    (e.target as HTMLInputElement).blur();
  };

  // disable decimals
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "." || e.key === ",") {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (isSuccess) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      refetch();
      hideModal();
      toast.success("Settings updated successfully");
    }
  }, [isSuccess, hideModal, refetch]);

  const footerButtons = () => (
    <>
      <Button
        className="btn btn-secondary btn-sm"
        size="sm"
        variant="secondary"
        onClick={hideModal}
      >
        Cancel
      </Button>
      <Button
        size="sm"
        className="btn btn-primary"
        disabled={
          isError ||
          isLoading ||
          !isValid.chargeWhenAmount ||
          !isValid.expectedAmount ||
          (chargeWhen === chargeWhenAmount &&
            expectedAmount === expectedAmountOnTheAccount &&
            isAutoChargeEnabled === isEnabled)
        }
        type="submit"
        onClick={updateBillingSettings}
      >
        Save settings
        {isLoading && <Spinner className="ms-2" size="sm" animation="border" />}
      </Button>
    </>
  );

  return (
    <CustomModal
      show={true}
      handleClose={hideModal}
      title="Auto recharge settings"
      footer={footerButtons()}
      errorMessage={isError ? "Error updating settings" : null}
      className={isEnabled ? "" : "auto-recharge-disabled"}
    >
      <Form.Switch
        className="custom-switch mb-7"
        label="Automatically recharge my card when my credit balance falls below a threshold"
        checked={isEnabled}
        onChange={handleChangeAutocharge}
        id="auto-recharge-switch"
      />

      <Form.Label htmlFor="charge-when">
        When credit balance goes below
      </Form.Label>
      <InputGroup className="input-with-text">
        <InputGroup.Text>$</InputGroup.Text>
        <Form.Control
          type="number"
          value={chargeWhenAmount || ""}
          onChange={handleChangeChargeWhenAmount}
          onKeyDown={handleKeyDown}
          disabled={!isEnabled}
          id="charge-when"
          onWheel={onScroll}
        />
      </InputGroup>
      <p
        className={`short-explanation mb-7 ${
          !isValid.chargeWhenAmount ? "invalid" : ""
        }`}
      >
        Enter an amount between ${minChargeWhenValue} and ${maxChargeWhenValue}
      </p>

      <Form.Label htmlFor="charge-amount">
        Bring credit balance back up to
      </Form.Label>
      <InputGroup className="input-with-text">
        <InputGroup.Text>$</InputGroup.Text>
        <Form.Control
          type="number"
          value={expectedAmount || ""}
          onChange={handleChangeExpectedAmount}
          onKeyDown={handleKeyDown}
          disabled={!isEnabled}
          id="charge-amount"
          onWheel={onScroll}
        />
      </InputGroup>
      <p
        className={`short-explanation mb-7 ${
          !isValid.expectedAmount ? "invalid" : ""
        }`}
      >
        Enter an amount between $
        {minimalExpectedTransaction +
          (isValid.chargeWhenAmount ? chargeWhenAmount : minChargeWhenValue)}
        &nbsp; and ${maxExpectedAmountValue}
      </p>
      {accBallance < chargeWhenAmount && isEnabled && (
        <>
          <h6 className="warning-recharge">
            <WarningIcon /> Recharge will happen immediately
          </h6>
          <p className="my-0">
            Your credit balance is below the recharge threshold you&#39;ve set,
            so a recharge will happen immediately after you save these settings.
          </p>
        </>
      )}
    </CustomModal>
  );
};

export { AutoRechargeModal };
